import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { sanitizeSearchInput, toValidObjectIdString } from '../utils/security';
import { fetchGarantiServisKayitlari, fetchAcikServisKayitlari } from '../services/siparisService';
import { fetchTechServiceDetail } from '../services/techServicesService';
import { fetchMobileServiceContents } from '../services/mobileAppointmentService';
import { getParcaFotoUrl } from '../services/productsService';
import './SiparisOlusturModal.css';
import { FieldLabel } from './FieldLabel';
import { parseAdet } from '../utils/integerInput';
import SiparisParcaWizardPanel from './SiparisParcaWizardPanel';
import SiparisParcaAdetModal from './SiparisParcaAdetModal';
import { createSiparis } from '../services/siparisService';
import {
  fetchFormTaslak,
  saveFormTaslak,
  deleteFormTaslak,
} from '../services/formTaslakService';
import {
  getSiparisTaslakDef,
  getSiparisTaslakTipFromFlags,
  taslakHasContent,
} from '../constants/siparisTaslak';
import {
  migrateLegacySiparisTaslakIfNeeded,
  enrichSecilenParcalarNetFiyat,
} from '../services/siparisTaslakService';
import { netFiyatFromParca } from '../utils/siparisNetFiyat';
import {
  persistSiparisDraftPhotos,
  restoreSiparisDraftPhotos,
  clearSiparisDraftPhotos,
} from '../utils/siparisDraftStorage';

const SIPARIS_MAX_FOTO = 8;
const DRAFT_DEBOUNCE_MS = 800;
const DRAFT_IMMEDIATE_MS = 0;

const SiparisOlusturModal = ({ onClose, onSuccess, taslakTip: initialTaslakTip, skipRestorePrompt = false }) => {
  const { user } = useAuth();
  const { confirm } = useConfirmDialog();
  const [activeTaslakTip, setActiveTaslakTip] = useState(initialTaslakTip || null);
  const [step, setStep] = useState(initialTaslakTip ? 2 : 1);
  const [garantiDahilinde, setGarantiDahilinde] = useState(() => {
    const def = getSiparisTaslakDef(initialTaslakTip);
    return def ? def.garantiDahilinde : null;
  });
  const [konsinye, setKonsinye] = useState(() => {
    const def = getSiparisTaslakDef(initialTaslakTip);
    return def ? def.konsinye : false;
  });
  const [servisKaydiArama, setServisKaydiArama] = useState('');
  const [servisKaydiAramaDebounce, setServisKaydiAramaDebounce] = useState('');
  const [servisKayitlari, setServisKayitlari] = useState([]);
  const [selectedServisKaydi, setSelectedServisKaydi] = useState(null);
  const [secilenParcalar, setSecilenParcalar] = useState([]);
  const [not, setNot] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [teknikServisAdresi, setTeknikServisAdresi] = useState('');
  const [farkliTeslimatAdresi, setFarkliTeslimatAdresi] = useState(false);
  const [ozelTeslimatAdresi, setOzelTeslimatAdresi] = useState('');
  /** @type {Array<{ file: File; previewUrl: string }>} */
  const [siparisFotograflar, setSiparisFotograflar] = useState([]);
  const [draftSavedHint, setDraftSavedHint] = useState(false);
  /** Seçilen parça satırında adet düzenleme modalı (icerikId) */
  const [parcaAdetEditId, setParcaAdetEditId] = useState(null);
  const [showAcikServisKaydiModal, setShowAcikServisKaydiModal] = useState(false);
  const [acikServisKayitlari, setAcikServisKayitlari] = useState([]);
  const [acikServisKayitlariLoading, setAcikServisKayitlariLoading] = useState(false);
  const [acikServisArama, setAcikServisArama] = useState('');
  const [acikServisAramaDebounce, setAcikServisAramaDebounce] = useState('');
  const [selectedDirectServisKaydi, setSelectedDirectServisKaydi] = useState(null);
  const [urunIcerikIdFilter, setUrunIcerikIdFilter] = useState(null);
  const loadedTaslakTipRef = useRef(null);
  const draftReadyRef = useRef(false);
  const saveDraftTimerRef = useRef(null);
  const siparisFotoRef = useRef(siparisFotograflar);

  useEffect(() => {
    siparisFotoRef.current = siparisFotograflar;
  }, [siparisFotograflar]);

  useEffect(() => () => {
    siparisFotoRef.current.forEach((x) => {
      if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
    });
  }, []);

  const buildDraftVeri = useCallback(
    () => ({
      step,
      garantiDahilinde,
      konsinye,
      servisKaydiId: selectedServisKaydi
        ? selectedServisKaydi.id || selectedServisKaydi._id
        : null,
      servisKaydiSnapshot: selectedServisKaydi || null,
      secilenParcalar,
      not,
      farkliTeslimatAdresi,
      ozelTeslimatAdresi,
      directServisKaydiSnapshot: selectedDirectServisKaydi || null,
    }),
    [
      step,
      garantiDahilinde,
      konsinye,
      selectedServisKaydi,
      secilenParcalar,
      not,
      farkliTeslimatAdresi,
      ozelTeslimatAdresi,
      selectedDirectServisKaydi,
    ],
  );

  const persistDraftNow = useCallback(async () => {
    if (!activeTaslakTip || !draftReadyRef.current) return;
    if (step === 1 && garantiDahilinde == null) return;
    try {
      const veri = buildDraftVeri();
      const empty =
        step <= 1 &&
        !secilenParcalar.length &&
        !not?.trim() &&
        !siparisFotograflar.length;
      if (empty) {
        await deleteFormTaslak(activeTaslakTip).catch(() => {});
        setDraftSavedHint(false);
        return;
      }
      await saveFormTaslak(activeTaslakTip, veri);
      setDraftSavedHint(true);
    } catch {
      /* */
    }
  }, [
    activeTaslakTip,
    step,
    garantiDahilinde,
    buildDraftVeri,
    secilenParcalar,
    not,
    siparisFotograflar,
  ]);

  const scheduleDraftSave = useCallback((immediate = false) => {
    if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    saveDraftTimerRef.current = setTimeout(
      () => {
        persistDraftNow();
      },
      immediate ? DRAFT_IMMEDIATE_MS : DRAFT_DEBOUNCE_MS,
    );
  }, [persistDraftNow]);

  const flushDraftSave = useCallback(async () => {
    if (saveDraftTimerRef.current) {
      clearTimeout(saveDraftTimerRef.current);
      saveDraftTimerRef.current = null;
    }
    await persistDraftNow();
  }, [persistDraftNow]);

  const handleRequestClose = useCallback(async () => {
    await flushDraftSave();
    onClose?.();
  }, [flushDraftSave, onClose]);

  useEffect(() => {
    if (!activeTaslakTip) {
      draftReadyRef.current = false;
      return undefined;
    }
    if (loadedTaslakTipRef.current === activeTaslakTip) return undefined;
    loadedTaslakTipRef.current = activeTaslakTip;
    draftReadyRef.current = false;

    (async () => {
      try {
        await migrateLegacySiparisTaslakIfNeeded();
        const [taslakRes, photos] = await Promise.all([
          fetchFormTaslak(activeTaslakTip),
          restoreSiparisDraftPhotos(activeTaslakTip),
        ]);
        const veri = taslakRes?.veri?.veri;
        const hasTaslak = taslakHasContent(veri, photos.length);
        if (!hasTaslak) {
          draftReadyRef.current = true;
          return;
        }
        if (!skipRestorePrompt) {
          const def = getSiparisTaslakDef(activeTaslakTip);
          const devam = await confirm({
            title: 'Kayıtlı taslak',
            message: `${def?.shortLabel || 'Sipariş'} taslağında kayıtlı veri var. Taslaktan devam etmek ister misiniz?`,
            variant: 'info',
            confirmLabel: 'Taslaktan devam et',
            cancelLabel: 'Yeni başla',
          });
          if (!devam) {
            draftReadyRef.current = true;
            return;
          }
        }
        if (veri.step) setStep(veri.step);
        if (veri.garantiDahilinde != null) setGarantiDahilinde(veri.garantiDahilinde);
        if (veri.konsinye != null) setKonsinye(!!veri.konsinye);
        if (veri.servisKaydiSnapshot) setSelectedServisKaydi(veri.servisKaydiSnapshot);
        if (Array.isArray(veri.secilenParcalar)) {
          const restored = veri.secilenParcalar;
          const isDogrudan =
            veri.garantiDahilinde === false && !veri.konsinye;
          if (isDogrudan && restored.some((p) => p.gorunenFiyat == null)) {
            enrichSecilenParcalarNetFiyat(restored).then(setSecilenParcalar);
          } else {
            setSecilenParcalar(restored);
          }
        }
        if (veri.not) setNot(veri.not);
        if (veri.farkliTeslimatAdresi != null) setFarkliTeslimatAdresi(!!veri.farkliTeslimatAdresi);
        if (veri.ozelTeslimatAdresi) setOzelTeslimatAdresi(veri.ozelTeslimatAdresi);
        if (veri.directServisKaydiSnapshot || veri.selectedDirectServisKaydi) {
          setSelectedDirectServisKaydi(
            veri.directServisKaydiSnapshot || veri.selectedDirectServisKaydi,
          );
        }
        if (photos.length) setSiparisFotograflar(photos);
      } catch {
        /* */
      } finally {
        draftReadyRef.current = true;
      }
    })();
  }, [activeTaslakTip, skipRestorePrompt, confirm]);

  /** Taslaktan veya stoktan eklenen satırlarda net fiyat eksikse içerik kartından tamamla */
  useEffect(() => {
    if (garantiDahilinde || konsinye) return undefined;
    if (!secilenParcalar.some((p) => p.gorunenFiyat == null && (p.icerikId || p.id))) {
      return undefined;
    }
    let cancelled = false;
    enrichSecilenParcalarNetFiyat(secilenParcalar).then((enriched) => {
      if (cancelled) return;
      const changed = enriched.some(
        (p, i) => p.gorunenFiyat !== secilenParcalar[i]?.gorunenFiyat,
      );
      if (changed) setSecilenParcalar(enriched);
    });
    return () => {
      cancelled = true;
    };
  }, [secilenParcalar, garantiDahilinde, konsinye]);

  useEffect(() => {
    if (!activeTaslakTip) return undefined;
    if (step === 1 && garantiDahilinde == null) return undefined;
    scheduleDraftSave(false);
    return () => {
      if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    };
  }, [
    activeTaslakTip,
    step,
    garantiDahilinde,
    konsinye,
    selectedServisKaydi,
    not,
    farkliTeslimatAdresi,
    ozelTeslimatAdresi,
    siparisFotograflar.length,
    scheduleDraftSave,
  ]);

  useEffect(() => {
    if (!activeTaslakTip) return undefined;
    if (step === 1 && garantiDahilinde == null) return undefined;
    scheduleDraftSave(true);
  }, [secilenParcalar, activeTaslakTip, step, garantiDahilinde, scheduleDraftSave]);

  useEffect(() => {
    if (!activeTaslakTip) return undefined;
    const t = setTimeout(() => {
      persistSiparisDraftPhotos(siparisFotograflar, activeTaslakTip).catch(() => {});
      scheduleDraftSave(true);
    }, 400);
    return () => clearTimeout(t);
  }, [siparisFotograflar, activeTaslakTip, scheduleDraftSave]);

  const birimNetFromParca = (parca) => netFiyatFromParca(parca);

  const tahminiToplamTutar = useMemo(() => {
    if (garantiDahilinde || konsinye) return null;
    let sum = 0;
    let any = false;
    for (const p of secilenParcalar) {
      const unit = p.gorunenFiyat != null ? parseFloat(p.gorunenFiyat) : NaN;
      if (!Number.isNaN(unit)) {
        any = true;
        sum += unit * (p.adet || 1);
      }
    }
    return any ? sum : null;
  }, [secilenParcalar, garantiDahilinde, konsinye]);

  // Teknik servis adresini yükle (tech-service kullanıcısı için)
  useEffect(() => {
    const tsId = user?.techService?._id || user?.techService || user?.techServiceId;
    if (!tsId) return;
    let cancelled = false;
    fetchTechServiceDetail(tsId)
      .then((res) => {
        if (cancelled) return;
        const ts = res?.veri?.teknikServis || res?.veri || res;
        setTeknikServisAdresi(ts?.adres || '');
      })
      .catch(() => {
        if (!cancelled) setTeknikServisAdresi('');
      });
    return () => { cancelled = true; };
  }, [user?.techService, user?.techServiceId]);

  // Debounce servis kaydı arama
  useEffect(() => {
    const t = setTimeout(() => setServisKaydiAramaDebounce(servisKaydiArama), 500);
    return () => clearTimeout(t);
  }, [servisKaydiArama]);

  // Garanti servis kayıtlarını yükle (arama yoksa önerilen 3, arama varsa 20)
  const loadServisKayitlari = useCallback(async () => {
    setLoading(true);
    try {
      const aramaTrim = servisKaydiAramaDebounce?.trim();
      const response = await fetchGarantiServisKayitlari({
        arama: aramaTrim || undefined,
        sayfa: 1,
        limit: aramaTrim ? 20 : 3,
      });
      setServisKayitlari(response?.veri?.servisKayitlari || []);
    } catch (err) {
      setServisKayitlari([]);
    } finally {
      setLoading(false);
    }
  }, [servisKaydiAramaDebounce]);

  useEffect(() => {
    if (step === 2 && garantiDahilinde === true) {
      loadServisKayitlari();
    }
  }, [step, garantiDahilinde, loadServisKayitlari]);

  useEffect(() => {
    const t = setTimeout(() => setAcikServisAramaDebounce(acikServisArama), 500);
    return () => clearTimeout(t);
  }, [acikServisArama]);

  const loadAcikServisKayitlari = useCallback(async () => {
    setAcikServisKayitlariLoading(true);
    try {
      const aramaTrim = acikServisAramaDebounce?.trim();
      const response = await fetchAcikServisKayitlari({
        arama: aramaTrim || undefined,
        sayfa: 1,
        limit: 50,
      });
      setAcikServisKayitlari(response?.veri?.servisKayitlari || []);
    } catch {
      setAcikServisKayitlari([]);
    } finally {
      setAcikServisKayitlariLoading(false);
    }
  }, [acikServisAramaDebounce]);

  useEffect(() => {
    if (!showAcikServisKaydiModal) return;
    loadAcikServisKayitlari();
  }, [showAcikServisKaydiModal, loadAcikServisKayitlari]);

  useEffect(() => {
    const sk = garantiDahilinde ? selectedServisKaydi : selectedDirectServisKaydi;
    if (!sk) {
      setUrunIcerikIdFilter(null);
      return undefined;
    }
    const skId = toValidObjectIdString(sk.id || sk._id);
    if (!skId) {
      setUrunIcerikIdFilter(null);
      return undefined;
    }
    let cancelled = false;
    fetchMobileServiceContents(skId)
      .then((res) => {
        if (cancelled) return;
        const urunId = res?.veri?.urunIcerikId || null;
        setUrunIcerikIdFilter(urunId ? String(urunId) : null);
      })
      .catch(() => {
        if (!cancelled) setUrunIcerikIdFilter(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedServisKaydi, selectedDirectServisKaydi, garantiDahilinde]);

  const openAcikServisKaydiModal = useCallback(() => {
    setAcikServisArama('');
    setAcikServisAramaDebounce('');
    setShowAcikServisKaydiModal(true);
  }, []);

  const handleSelectAcikServisKaydi = useCallback((sk) => {
    setSelectedDirectServisKaydi(sk);
    setShowAcikServisKaydiModal(false);
  }, []);

  const clearSelectedDirectServisKaydi = useCallback(() => {
    setSelectedDirectServisKaydi(null);
  }, []);

  const handleParcaEkle = (parca, adet = 1) => {
    const id = parca.id || parca._id;
    const existing = secilenParcalar.find((p) => (p.icerikId || p.id) === id);
    const gorunenFiyat = (garantiDahilinde || konsinye) ? null : birimNetFromParca(parca);
    if (existing) {
      setSecilenParcalar((prev) =>
        prev.map((p) =>
          (p.icerikId || p.id) === id ? { ...p, adet: (p.adet || 1) + adet } : p
        )
      );
    } else {
      setSecilenParcalar((prev) => [
        ...prev,
        {
          icerikId: id,
          parcaAdi: parca.icerikAdi || parca.parcaAdi || parca.urunAdi || parca.ad,
          parcaKodu: parca.icerikKodu || parca.parcaKodu || parca.urunKodu || parca.kod,
          gorunenFiyat,
          adet,
          fotoUrl: parca.fotoUrl || null,
        },
      ]);
    }
  };

  const onFotoInputChange = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (!picked.length) return;
    setSiparisFotograflar((prev) =>
      [
        ...prev,
        ...picked.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ].slice(0, SIPARIS_MAX_FOTO)
    );
    e.target.value = '';
  };

  const handleParcaAdetChange = (icerikId, adet) => {
    const num = parseAdet(adet, { min: 1 });
    setSecilenParcalar((prev) =>
      prev.map((p) => ((p.icerikId || p.id) === icerikId ? { ...p, adet: num } : p))
    );
  };

  const handleParcaRemove = (icerikId) => {
    setSecilenParcalar((prev) => prev.filter((p) => (p.icerikId || p.id) !== icerikId));
    if (parcaAdetEditId != null && String(parcaAdetEditId) === String(icerikId)) {
      setParcaAdetEditId(null);
    }
  };

  const parcaAdetEditTarget = useMemo(() => {
    if (parcaAdetEditId == null) return null;
    return (
      secilenParcalar.find(
        (p) => String(p.icerikId || p.id) === String(parcaAdetEditId),
      ) || null
    );
  }, [parcaAdetEditId, secilenParcalar]);

  const fotoDosyaInputRef = useRef(null);

  const removeFotoAt = (idx) => {
    setSiparisFotograflar((prev) => {
      const removed = prev[idx];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const renderFotoSection = (compact = false) => (
    <div
      className={`siparis-modal-form-group siparis-modal-foto-block${compact ? ' siparis-modal-foto-block--step' : ''}`}
    >
      <label>Fotoğraf ekle</label>
      <p className="siparis-modal-foto-hint">
        İsteğe bağlı — en fazla {SIPARIS_MAX_FOTO} görsel (JPEG, PNG, GIF, WebP)
      </p>
      <input
        ref={fotoDosyaInputRef}
        type="file"
        className="siparis-modal-foto-input-hidden"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={onFotoInputChange}
        disabled={siparisFotograflar.length >= SIPARIS_MAX_FOTO}
      />
      <button
        type="button"
        className="btn btn-secondary siparis-modal-foto-add-btn"
        onClick={() => fotoDosyaInputRef.current?.click()}
        disabled={siparisFotograflar.length >= SIPARIS_MAX_FOTO}
      >
        <i className="fas fa-camera" aria-hidden />
        Fotoğraf seç
      </button>
      {siparisFotograflar.length > 0 && (
        <div className="siparis-modal-foto-grid">
          {siparisFotograflar.map((item, i) => (
            <div key={`${item.previewUrl}-${i}`} className="siparis-modal-foto-tile">
              <img src={item.previewUrl} alt="" className="siparis-modal-foto-thumb" />
              <button
                type="button"
                className="siparis-modal-foto-remove"
                onClick={() => removeFotoAt(i)}
                title="Kaldır"
                aria-label="Fotoğrafı kaldır"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleSubmit = async () => {
    const hasParts = secilenParcalar.length > 0;
    const fotoFiles = siparisFotograflar.map((x) => x.file).filter(Boolean);
    const hasFotos = fotoFiles.length > 0;
    if (!hasParts && !hasFotos) {
      setError('En az bir parça veya en az bir fotoğraf ekleyin');
      return;
    }
    if (farkliTeslimatAdresi && !ozelTeslimatAdresi?.trim()) {
      setError('Farklı teslimat adresi seçtiniz, lütfen teslimat adresini girin');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const istekPozisyonlari = [];
      for (const p of secilenParcalar) {
        const icerikId = toValidObjectIdString(p.icerikId || p.id);
        if (!icerikId) {
          setError(`"${p.parcaAdi || 'Parça'}" için geçersiz ID formatı. Lütfen sayfayı yenileyip tekrar deneyin.`);
          setSubmitting(false);
          return;
        }
        istekPozisyonlari.push({ icerikId, adet: p.adet || 1 });
      }
      let servisKaydiId;
      if (garantiDahilinde && selectedServisKaydi) {
        servisKaydiId = toValidObjectIdString(selectedServisKaydi.id || selectedServisKaydi._id);
        if (!servisKaydiId) {
          setError('Seçilen servis kaydı için geçersiz ID formatı. Lütfen servis kaydını yeniden seçin.');
          setSubmitting(false);
          return;
        }
      } else if (!garantiDahilinde && !konsinye && selectedDirectServisKaydi) {
        servisKaydiId = toValidObjectIdString(
          selectedDirectServisKaydi.id || selectedDirectServisKaydi._id,
        );
        if (!servisKaydiId) {
          setError('Seçilen servis kaydı için geçersiz ID formatı.');
          setSubmitting(false);
          return;
        }
      }
      const payload = {
        garantiDahilinde: !!garantiDahilinde,
        konsinye: !!konsinye,
        istekPozisyonlari,
        not: not.trim() || undefined,
      };
      if (servisKaydiId) payload.servisKaydiId = servisKaydiId;
      if (!garantiDahilinde && !konsinye && selectedDirectServisKaydi?.cihazId) {
        const cid = toValidObjectIdString(selectedDirectServisKaydi.cihazId);
        if (cid) payload.cihazId = cid;
      }
      if (farkliTeslimatAdresi && ozelTeslimatAdresi?.trim()) {
        payload.teslimatAdresi = ozelTeslimatAdresi.trim();
      } else if (teknikServisAdresi?.trim()) {
        payload.teslimatAdresi = teknikServisAdresi.trim();
      }
      const response = await createSiparis(payload, hasFotos ? fotoFiles : null);
      if (response?.basarili) {
        if (activeTaslakTip) {
          await deleteFormTaslak(activeTaslakTip).catch(() => {});
          clearSiparisDraftPhotos(activeTaslakTip);
        }
        window.showNotification?.('success', 'Sipariş başarıyla oluşturuldu');
        onSuccess?.();
        onClose();
      } else {
        setError(response?.mesaj || 'Sipariş oluşturulamadı');
      }
    } catch (err) {
      if (err?.data?.kod === 'CARI_LIMIT_ASIMI' || err?.data?.detaylar) {
        const d = err.data.detaylar || {};
        setError(
          `Konsinye cari limiti aşıldı. Limit: ${d.cariLimit ?? '—'} ₺, `
          + `kullanım: ${d.kullanimTutar ?? '—'} ₺, `
          + `müsait: ${d.musaitTutar ?? '—'} ₺`
        );
      } else {
        setError(err?.message || 'Sipariş oluşturulamadı');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (d, saat) => {
    if (!d && !saat) return '—';
    try {
      const date = d ? (typeof d === 'string' ? new Date(d) : d) : null;
      const tarihStr = date && !isNaN(date.getTime()) ? date.toLocaleDateString('tr-TR') : '';
      const saatStr = saat && typeof saat === 'string' ? saat.trim() : '';
      if (tarihStr && saatStr) return `${tarihStr} ${saatStr}`;
      if (tarihStr) return tarihStr;
      return saatStr || '—';
    } catch {
      return '—';
    }
  };

  const applySiparisType = (garanti, kons) => {
    setGarantiDahilinde(garanti);
    setKonsinye(kons);
    setActiveTaslakTip(getSiparisTaslakTipFromFlags(garanti, kons));
    setStep(2);
  };

  const taslakDef = getSiparisTaslakDef(activeTaslakTip);
  const modalTitle = taslakDef ? `${taslakDef.shortLabel} — Sipariş` : 'Yeni Sipariş Oluştur';
  const isTypeStep = step === 1 && !initialTaslakTip;
  const isServisKaydiStep = step === 2 && garantiDahilinde === true && !konsinye;
  const isPartsStep =
    (step === 2 && (garantiDahilinde === false || konsinye === true)) ||
    (step === 3 && garantiDahilinde === true && !konsinye);
  const isFotoStep =
    (step === 3 && (garantiDahilinde === false || konsinye === true)) ||
    (step === 4 && garantiDahilinde === true && !konsinye);
  const isTeslimatStep =
    (step === 4 && (garantiDahilinde === false || konsinye === true)) ||
    (step === 5 && garantiDahilinde === true && !konsinye);

  const getOrderWizardInfo = () => {
    if (garantiDahilinde && !konsinye) {
      if (step === 3) return { current: 1, total: 3, label: 'Parçalar' };
      if (step === 4) return { current: 2, total: 3, label: 'Fotoğraf' };
      if (step === 5) return { current: 3, total: 3, label: 'Teslimat ve not' };
    }
    if (garantiDahilinde === false || konsinye === true) {
      if (step === 2) return { current: 1, total: 3, label: 'Parçalar' };
      if (step === 3) return { current: 2, total: 3, label: 'Fotoğraf' };
      if (step === 4) return { current: 3, total: 3, label: 'Teslimat ve not' };
    }
    return null;
  };

  const renderWizardProgress = () => {
    const wizard = getOrderWizardInfo();
    if (!wizard) return null;
    return (
      <div className="siparis-modal-wizard-progress" aria-label={`Adım ${wizard.current} / ${wizard.total}`}>
        <FieldLabel className="siparis-modal-wizard-progress-label" as="span">
          {`Adım ${wizard.current} / ${wizard.total} — ${wizard.label}`}
        </FieldLabel>
        <div className="siparis-modal-wizard-progress-dots">
          {Array.from({ length: wizard.total }, (_, i) => (
            <span
              key={i}
              className={`siparis-modal-wizard-dot${i + 1 <= wizard.current ? ' is-active' : ''}${
                i + 1 === wizard.current ? ' is-current' : ''
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  const goToFotoStep = () => {
    setError('');
    setStep(garantiDahilinde && !konsinye ? 4 : 3);
  };

  const goToTeslimatStep = () => {
    setError('');
    setStep(garantiDahilinde && !konsinye ? 5 : 4);
  };

  const goBackFromParts = () => {
    setError('');
    if (garantiDahilinde && !konsinye) {
      setStep(2);
    } else if (initialTaslakTip) {
      handleRequestClose();
    } else {
      setStep(1);
    }
  };

  const goBackFromFoto = () => {
    setError('');
    setStep(garantiDahilinde && !konsinye ? 3 : 2);
  };

  const goBackFromTeslimat = () => {
    setError('');
    setStep(garantiDahilinde && !konsinye ? 4 : 3);
  };

  const renderWizardFooter = ({ onBack, onPrimary, primaryLabel, primaryDisabled, primaryLoading }) => (
    <div className="siparis-modal-footer-btns">
      <button type="button" className="btn btn-secondary" onClick={onBack}>
        <i className="fas fa-arrow-left" /> Geri
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onPrimary}
        disabled={primaryDisabled || primaryLoading}
      >
        {primaryLoading ? (
          <>
            <i className="fas fa-spinner fa-spin" /> Gönderiliyor...
          </>
        ) : (
          primaryLabel
        )}
      </button>
    </div>
  );

  const formatParcaCurrency = (amount) => {
    if (amount == null || Number.isNaN(parseFloat(amount))) return '—';
    return `${parseFloat(amount).toFixed(2)} ₺`;
  };

  const renderSecilenParcalarTable = (showPrice, highlightPartId = null) => (
    <div className="parca-sec-table-wrap parca-sec-table-wrap--qty-first">
      {secilenParcalar.length === 0 ? (
        <div className="parca-sec-empty-positions">
          Henüz parça eklenmedi. Soldaki katalogdan «+» ile ekleyin.
        </div>
      ) : (
        <table className="admins-table parca-sec-selected-table">
          <colgroup>
            <col className="parca-sec-col-thumb" />
            <col className="parca-sec-col-qty" />
            <col />
            <col />
            {showPrice ? <col className="parca-sec-col-price" /> : null}
            <col className="parca-sec-col-action" />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="Görsel" />
              <th className="parca-sec-th-qty">Adet</th>
              <th>Parça</th>
              <th>Kod</th>
              {showPrice ? <th className="parca-sec-th-price">Net fiyat</th> : null}
              <th className="parca-sec-th-action" aria-label="İşlem" />
            </tr>
          </thead>
          <tbody>
            {secilenParcalar.map((p) => {
              const rowId = p.icerikId || p.id;
              const isHighlight =
                highlightPartId && String(rowId) === String(highlightPartId);
              const thumbSrc = getParcaFotoUrl(p.fotoUrl);
              return (
                <tr
                  key={rowId}
                  className={`parca-sec-selected-row${isHighlight ? ' is-just-added' : ''}${
                    parcaAdetEditId != null && String(parcaAdetEditId) === String(rowId)
                      ? ' is-adet-editing'
                      : ''
                  }`}
                  onClick={() => setParcaAdetEditId(rowId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setParcaAdetEditId(rowId);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${p.parcaAdi || 'Parça'}, adet ${p.adet || 1} — düzenle`}
                >
                  <td className="parca-sec-td-thumb">
                    {thumbSrc ? (
                      <img src={thumbSrc} alt="" className="siparis-parca-picker__thumb" />
                    ) : (
                      <span className="siparis-parca-picker__thumb-placeholder" title="Görsel yok">
                        <i className="fas fa-image" aria-hidden />
                      </span>
                    )}
                  </td>
                  <td className="parca-sec-td-qty">
                    <span className="parca-sec-qty-display">{p.adet || 1}</span>
                  </td>
                  <td>{p.parcaAdi || '—'}</td>
                  <td>{p.parcaKodu || '—'}</td>
                  {showPrice ? (
                    <td className="parca-sec-td-price">{formatParcaCurrency(p.gorunenFiyat)}</td>
                  ) : null}
                  <td className="parca-sec-td-action">
                    <button
                      type="button"
                      className="category-action-btn delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleParcaRemove(rowId);
                      }}
                      title="Kaldır"
                      aria-label="Parçayı kaldır"
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderTeslimatSection = () => (
    <div className="siparis-modal-form-group">
      <label>Teslimat Adresi</label>
      <div style={{ fontSize: 13, color: 'var(--text-gray)', marginBottom: 8 }}>
        {teknikServisAdresi || 'Teknik servis adresi yükleniyor...'}
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          fontWeight: 400,
          marginBottom: farkliTeslimatAdresi ? 8 : 0,
        }}
      >
        <input
          type="checkbox"
          checked={farkliTeslimatAdresi}
          onChange={(e) => setFarkliTeslimatAdresi(e.target.checked)}
        />
        Farklı bir teslimat adresi istiyorum
      </label>
      {farkliTeslimatAdresi && (
        <textarea
          value={ozelTeslimatAdresi}
          onChange={(e) => setOzelTeslimatAdresi(e.target.value)}
          rows={3}
          placeholder="Teslimat adresini yazın..."
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );

  const renderEslestirSection = () => {
    if (garantiDahilinde || konsinye) return null;
    if (selectedDirectServisKaydi) {
      return (
        <div className="siparis-modal-sk-selected">
          <div className="siparis-modal-sk-selected-head">
            <div>
              <strong>
                {selectedDirectServisKaydi.numara ? `${selectedDirectServisKaydi.numara} · ` : ''}
                {selectedDirectServisKaydi.islemTipi || 'Servis kaydı'}
              </strong>
              <p>
                {selectedDirectServisKaydi.musteriAdi || '—'}
                {selectedDirectServisKaydi.cihazSeriNumarasi
                  ? ` · SN: ${selectedDirectServisKaydi.cihazSeriNumarasi}`
                  : ''}
              </p>
            </div>
            <button
              type="button"
              className="siparis-modal-sk-selected-clear"
              onClick={clearSelectedDirectServisKaydi}
              aria-label="Servis kaydı seçimini kaldır"
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <button
            type="button"
            className="btn btn-link siparis-modal-sk-change-btn"
            onClick={openAcikServisKaydiModal}
          >
            Servis kaydını değiştir
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="siparis-modal-sk-select-btn"
        onClick={openAcikServisKaydiModal}
      >
        <i className="fas fa-clipboard-list" />
        <span>
          <strong>Servis kaydı seç</strong>
          <small>Açık servis kayıtlarından seçim yapın</small>
        </span>
        <i className="fas fa-chevron-right" />
      </button>
    );
  };

  const renderAcikServisKaydiModal = () => {
    if (!showAcikServisKaydiModal) return null;
    const selectedId = toValidObjectIdString(
      selectedDirectServisKaydi?.id || selectedDirectServisKaydi?._id,
    );
    return (
      <div
        className="modal-overlay siparis-sk-picker-overlay"
        onClick={() => setShowAcikServisKaydiModal(false)}
      >
        <div
          className="modal-content siparis-sk-picker-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Açık servis kayıtları</h2>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowAcikServisKaydiModal(false)}
              aria-label="Kapat"
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="siparis-modal-search-wrap">
              <i className="fas fa-search search-icon" />
              <input
                type="text"
                className="siparis-modal-input"
                placeholder="Servis kaydı ara"
                value={acikServisArama}
                onChange={(e) => setAcikServisArama(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
              />
            </div>
            {acikServisKayitlariLoading ? (
              <div className="siparis-modal-empty">
                <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
                Yükleniyor...
              </div>
            ) : acikServisKayitlari.length === 0 ? (
              <div className="siparis-modal-empty">Açık servis kaydı bulunamadı</div>
            ) : (
              <div className="siparis-sk-picker-list">
                {acikServisKayitlari.map((sk) => {
                  const skid = toValidObjectIdString(sk?.id || sk?._id);
                  if (!skid) return null;
                  const selected = selectedId === skid;
                  return (
                    <button
                      key={skid}
                      type="button"
                      className={`siparis-sk-picker-item${selected ? ' is-selected' : ''}`}
                      onClick={() => handleSelectAcikServisKaydi(sk)}
                    >
                      <span className="siparis-sk-picker-item-title">
                        {sk.numara ? `${sk.numara} · ` : ''}
                        {sk.islemTipi || 'Servis kaydı'}
                      </span>
                      <span className="siparis-sk-picker-item-meta">
                        {sk.musteriAdi || '—'}
                        {sk.cihazSeriNumarasi ? ` · SN: ${sk.cihazSeriNumarasi}` : ''}
                      </span>
                      {sk.durum ? (
                        <span className="siparis-sk-picker-item-durum">{sk.durum}</span>
                      ) : null}
                      {selected ? <i className="fas fa-check-circle siparis-sk-picker-check" /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPartsStep = (showPrice) => (
    <div className="siparis-modal-parca-step siparis-modal-parca-step--wizard">
      {renderWizardProgress()}
      <p className="siparis-modal-step-desc">
        {konsinye
          ? 'Konsinye siparişte fiyat oluşturulmaz; parçalar randevuda kullanıldıkça mevcut fiyattan faturalandırılır.'
          : showPrice
            ? 'Parça seçin; tabloda net fiyat (fiyatgaranti) gösterilir, kesin tutar onayda işlenir.'
            : 'Garanti dahilinde parçaları seçin.'}
      </p>
      {renderEslestirSection()}
      <SiparisParcaWizardPanel
        showPrice={showPrice}
        secilenParcalar={secilenParcalar}
        onAddPart={handleParcaEkle}
        birimFiyatFromParca={showPrice ? birimNetFromParca : undefined}
        eslesenUrunIcerikId={urunIcerikIdFilter}
        renderSelectedPanel={(highlightPartId) =>
          renderSecilenParcalarTable(showPrice, highlightPartId)
        }
      />
      <SiparisParcaAdetModal
        open={parcaAdetEditTarget != null}
        parca={parcaAdetEditTarget}
        onAdetChange={handleParcaAdetChange}
        onClose={() => setParcaAdetEditId(null)}
      />
      {showPrice && tahminiToplamTutar != null && (
        <div className="siparis-modal-total-strip" role="status">
          <FieldLabel className="siparis-modal-total-strip-label" as="span">Toplam Tutar</FieldLabel>
          <span className="siparis-modal-total-strip-value">{tahminiToplamTutar.toFixed(2)} ₺</span>
        </div>
      )}
      {renderWizardFooter({
        onBack: goBackFromParts,
        onPrimary: goToFotoStep,
        primaryLabel: (
          <>
            Devam <i className="fas fa-arrow-right" />
          </>
        ),
      })}
    </div>
  );

  const renderFotoStep = () => (
    <div className="siparis-modal-wizard-step">
      {renderWizardProgress()}
      <p className="siparis-modal-step-desc">
        Siparişe fotoğraf ekleyebilirsiniz. En az bir parça veya en az bir fotoğraf ile sipariş verilebilir.
      </p>
      {(secilenParcalar.length > 0 || siparisFotograflar.length > 0) && (
        <div className="siparis-modal-wizard-summary">
          {secilenParcalar.length > 0 && (
            <span>
              <i className="fas fa-cog" /> {secilenParcalar.length} parça seçildi
            </span>
          )}
          {siparisFotograflar.length > 0 && (
            <span>
              <i className="fas fa-camera" /> {siparisFotograflar.length} fotoğraf
            </span>
          )}
        </div>
      )}
      {renderFotoSection(true)}
      {renderWizardFooter({
        onBack: goBackFromFoto,
        onPrimary: goToTeslimatStep,
        primaryLabel: (
          <>
            Devam <i className="fas fa-arrow-right" />
          </>
        ),
      })}
    </div>
  );

  const renderTeslimatStep = () => (
    <div className="siparis-modal-wizard-step">
      {renderWizardProgress()}
      <p className="siparis-modal-step-desc">Teslimat adresini kontrol edin ve isteğe bağlı not ekleyin.</p>
      {(secilenParcalar.length > 0 || siparisFotograflar.length > 0) && (
        <div className="siparis-modal-wizard-summary">
          {secilenParcalar.length > 0 && (
            <span>
              <i className="fas fa-cog" /> {secilenParcalar.length} parça
            </span>
          )}
          {siparisFotograflar.length > 0 && (
            <span>
              <i className="fas fa-camera" /> {siparisFotograflar.length} fotoğraf
            </span>
          )}
          {!garantiDahilinde && !konsinye && tahminiToplamTutar != null && (
            <span>Tahmini {tahminiToplamTutar.toFixed(2)} ₺</span>
          )}
        </div>
      )}
      {renderTeslimatSection()}
      <div className="siparis-modal-form-group">
        <label>Not (opsiyonel)</label>
        <textarea value={not} onChange={(e) => setNot(e.target.value)} rows={3} />
      </div>
      {error && <div className="siparis-modal-error">{error}</div>}
      {renderWizardFooter({
        onBack: goBackFromTeslimat,
        onPrimary: handleSubmit,
        primaryLabel: (
          <>
            <i className="fas fa-paper-plane" /> Sipariş Oluştur
          </>
        ),
        primaryDisabled: secilenParcalar.length === 0 && siparisFotograflar.length === 0,
        primaryLoading: submitting,
      })}
    </div>
  );

  return (
    <>
    <div
      className={`modal-overlay siparis-modal${isTypeStep ? ' siparis-modal--type' : ''}${isPartsStep ? ' siparis-modal--parts' : ''}${isFotoStep ? ' siparis-modal--foto' : ''}${isTeslimatStep ? ' siparis-modal--teslimat' : ''}`}
      onClick={handleRequestClose}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{modalTitle}</h2>
          {draftSavedHint && activeTaslakTip && (
            <span className="siparis-modal-draft-hint">Taslak kaydedildi</span>
          )}
          <button type="button" className="modal-close-btn" onClick={handleRequestClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {step === 1 && (
            <div>
              <p className="siparis-modal-step-desc">Sipariş türünü seçin:</p>
              <div className="siparis-modal-type-btns">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => applySiparisType(true, false)}
                >
                  <i className="fas fa-shield-alt"></i>
                  Garanti Dahilinde
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => applySiparisType(false, false)}
                >
                  <i className="fas fa-shopping-cart"></i>
                  Doğrudan Sipariş
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => applySiparisType(false, true)}
                >
                  <i className="fas fa-box-open"></i>
                  Konsinye
                </button>
              </div>
            </div>
          )}

          {isServisKaydiStep && (
            <div>
              <p className="siparis-modal-step-desc">
                Garanti dahilinde servis kayıtlarından birini seçin (randevu iptal edilenler hariç):
              </p>
              <div className="siparis-modal-search-wrap">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="siparis-modal-input"
                  placeholder="Servis kaydı veya müşteri ile ara..."
                  value={servisKaydiArama}
                  onChange={(e) => setServisKaydiArama(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
                />
              </div>
              {loading ? (
                <div className="siparis-modal-empty">
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                  Yükleniyor...
                </div>
              ) : servisKayitlari.length === 0 ? (
                <div className="siparis-modal-empty">Uygun servis kaydı bulunamadı</div>
              ) : (
                <div className="siparis-modal-randevu-list">
                  {servisKayitlari.map((sk) => (
                    <div
                      key={sk.id}
                      className={`siparis-modal-randevu-item ${selectedServisKaydi?.id === sk.id ? 'selected' : ''}`}
                      onClick={() => setSelectedServisKaydi(sk)}
                    >
                      <div className="randevu-no">
                        {sk.numara ? `${sk.numara} — ` : ''}{sk.islemTipi} — {formatDateTime(sk.olusturmaTarihi)}
                      </div>
                      <div className="randevu-meta">{sk.musteriAdi} | {sk.musteriTelefon || '—'}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="siparis-modal-footer-btns">
                <button type="button" className="btn btn-secondary" onClick={() => (initialTaslakTip ? handleRequestClose() : setStep(1))}>
                  <i className="fas fa-arrow-left"></i> Geri
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!selectedServisKaydi}
                  onClick={() => setStep(3)}
                >
                  Devam <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          )}

          {isPartsStep && renderPartsStep(!garantiDahilinde && !konsinye)}

          {isFotoStep && renderFotoStep()}

          {isTeslimatStep && renderTeslimatStep()}
        </div>
      </div>
    </div>
    {renderAcikServisKaydiModal()}
    </>
  );
};

export default SiparisOlusturModal;
